// src/pages/ReportIssue/ReportIssueModernClaude.jsx
import React, {useState} from "react";
import {
    Box,
    Button as MuiButton,
    Backdrop,
    CircularProgress,
    Card,
    CardContent,
    CardHeader,
    Alert,
    Stepper,
    Step,
    StepLabel,
    Typography,
    Divider
} from "@mui/material";
import {Formik, Form} from "formik";
import * as yup from "yup";
import {
    BuildCircle as BuildIcon,
    ArrowBack as ArrowBackIcon,
} from "@mui/icons-material";
import {useNavigate} from "react-router-dom";
import CryptoJS from "crypto-js";
import {KEY} from "../../dummyData";
import {useFeatureFlags} from "../../context/FeatureFlagsContext";
import DisabledFeatureBanner from "../../components/FeatureGate/DisabledFeatureBanner";

// Import custom components
import PersonalDetailsSection from "./components/PersonalDetailsSection";
import IssueDetailsSection from "./components/IssueDetailsSection";
import DescriptionSection from "./components/DescriptionSection";
import PhotosSection from "./components/PhotoSection";
import IssueSummarySection from "./components/IssueSummarySection";
import SuccessMessage from "./components/SuccessMessage";

// Import custom hook
import useIssueSubmission from "./components/useIssueSubmission";

const ReportIssueModernClaude = (props) => {
    const { isEnabled, getMessage } = useFeatureFlags();
    const navigate = useNavigate();

    // Get tenant details from localStorage
    const tenantDetails = React.useMemo(() => {
        try {
            let encryptedDetails = localStorage.getItem("tenant_details");
            if (encryptedDetails) {
                let details = CryptoJS.AES.decrypt(encryptedDetails, KEY);
                let originalText = details.toString(CryptoJS.enc.Utf8);
                if (originalText) {
                    return new Map(Object.entries(JSON.parse(originalText)));
                }
            }
            return new Map();
        } catch (error) {
            console.error("Error decrypting tenant details:", error);
            return new Map();
        }
    }, []);

    // State for multi-step form
    const [activeStep, setActiveStep] = useState(0);

    // Ref to the form submit function
    const formRef = React.useRef();

    // Get issue submission functionality from custom hook
    const {
        problemTypes,
        issues,
        selectedProblemType,
        selectedIssue,
        selectedPriority,
        editorState,
        photos,
        loadingProblemTypes,
        loadingIssues,
        isSubmitting,
        submitSuccess,
        submitError,
        errorMessage,
        setSelectedProblemType,
        setSelectedIssue,
        setSelectedPriority,
        setEditorState,
        setSubmitSuccess,
        setSubmitError,
        fetchProblemTypes,
        getIssues,
        handlePhotoUpload,
        removePhoto,
        submitIssue,
        handleReset,
        showSnackBar
    } = useIssueSubmission(props, tenantDetails);

    // Initialize issue types on component mount
    React.useEffect(() => {
        fetchProblemTypes();
    }, [fetchProblemTypes]);

    // Form validation schema
    const validationSchema = yup.object().shape({
        phone_number: yup.string().required("Phone number is required"),
    });

    // Initial form values
    const initialValues = {
        first_name: tenantDetails.get("first_name") || "",
        last_name: tenantDetails.get("last_name") || "",
        phone_number: tenantDetails.get("phone_number") || "",
        national_id: tenantDetails.get("national_id") || "",
        unit: tenantDetails.get("house_number") || "",
        property_name: tenantDetails.get("property_name") || "",
    };

    // Validate form before moving to step 2
    const validateStep1 = () => {
        if (selectedProblemType === "") {
            showSnackBar("Please select the type of problem.", "error");
            return false;
        }

        if (selectedIssue === "") {
            showSnackBar("Please select the specific issue.", "error");
            return false;
        }

        const content = editorState.getCurrentContent();
        if (!content.hasText()) {
            showSnackBar("Please provide additional information about the issue.", "error");
            return false;
        }

        if (selectedPriority === "") {
            showSnackBar("Please select a priority level.", "error");
            return false;
        }

        return true;
    };

    // Handle next step
    const handleNext = () => {
        if (activeStep === 0) {
            if (validateStep1()) {
                setActiveStep(1);
            }
        } else {
            // For step 2, submit the form programmatically
            if (formRef.current) {
                formRef.current.handleSubmit();
            }
        }
    };

    // Handle back step
    const handleBack = () => {
        setActiveStep(0);
    };

    // Handle form submission - actual submission will only happen in step 2
    const handleSubmitForm = () => {
        if (activeStep === 1) {
            submitIssue(tenantDetails);
        }
    };

    if (!isEnabled('support_tickets_create')) {
        return <DisabledFeatureBanner featureName="Raise Issue" message={getMessage('support_tickets_create')} />;
    }

    return (
        <Box sx={{margin: "10px"}}>
            {/* Loading Overlay */}
            <Backdrop
                sx={{color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1}}
                open={isSubmitting}
            >
                <CircularProgress color="inherit"/>
            </Backdrop>

            {/* Success Message */}
            {submitSuccess && activeStep !== 1 && (
                <Alert
                    severity="success"
                    sx={{mb: 3, borderRadius: '10px'}}
                    onClose={() => setSubmitSuccess(false)}
                >
                    Your issue has been successfully logged. We will get back to you shortly.
                </Alert>
            )}

            {/* Error Message */}
            {submitError && (
                <Alert
                    severity="error"
                    sx={{mb: 3, borderRadius: '10px'}}
                    onClose={() => setSubmitError(false)}
                >
                    {errorMessage}
                </Alert>
            )}

            {/* Back Navigation */}
            <Box sx={{display: 'flex', alignItems: 'center', mb: 3}}>
                <MuiButton
                    variant="text"
                    color="inherit"
                    startIcon={<ArrowBackIcon/>}
                    onClick={() => navigate('/tickets')}
                    sx={{mr: 2, textTransform: 'none'}}
                    disabled={isSubmitting}
                >
                    Back to Tickets
                </MuiButton>

                <Typography variant="h5" sx={{fontWeight: 600}}>
                    Report New Issue
                </Typography>
            </Box>

            {/* Stepper */}
            <Stepper activeStep={activeStep} sx={{mb: 3}}>
                <Step>
                    <StepLabel>Issue Details</StepLabel>
                </Step>
                <Step>
                    <StepLabel>Attachments & Review</StepLabel>
                </Step>
            </Stepper>

            {/* Main Card */}
            <Card
                elevation={0}
                sx={{
                    borderRadius: '16px',
                    border: '1px solid #E5E7EB',
                    overflow: 'hidden'
                }}
            >
                <CardHeader
                    title={
                        <Box display="flex" alignItems="center" gap={1}>
                            <BuildIcon sx={{color: '#398DD2'}}/>
                            <Typography variant="h5" sx={{fontWeight: 600, color: '#3A5A78'}}>
                                {activeStep === 0 ? 'Log Issue' : 'Review & Submit'}
                            </Typography>
                        </Box>
                    }
                    sx={{
                        backgroundColor: '#F8FAFC',
                        borderBottom: '1px solid #E5E7EB',
                        p: 3
                    }}
                />

                <CardContent sx={{p: 3}}>
                    <Formik
                        innerRef={formRef}
                        initialValues={initialValues}
                        validationSchema={validationSchema}
                        onSubmit={handleSubmitForm}
                    >
                        {(formikProps) => (
                            <Form noValidate>
                                {activeStep === 0 ? (
                                    // Step 1: Issue Details
                                    <>
                                        <PersonalDetailsSection
                                            formikProps={formikProps}
                                        />
                                        <Divider sx={{my: 4}}/>

                                        <IssueDetailsSection
                                            problemTypes={problemTypes}
                                            issues={issues}
                                            selectedProblemType={selectedProblemType}
                                            selectedIssue={selectedIssue}
                                            selectedPriority={selectedPriority}
                                            loadingProblemTypes={loadingProblemTypes}
                                            loadingIssues={loadingIssues}
                                            setSelectedProblemType={setSelectedProblemType}
                                            setSelectedIssue={setSelectedIssue}
                                            setSelectedPriority={setSelectedPriority}
                                            getIssues={getIssues}
                                        />
                                        <Divider sx={{my: 4}}/>

                                        <DescriptionSection
                                            editorState={editorState}
                                            setEditorState={setEditorState}
                                        />
                                    </>
                                ) : (
                                    // Step 2: Attachments & Review
                                    submitSuccess ? (
                                        <SuccessMessage navigate={navigate}/>
                                    ) : (
                                        <>
                                            <PhotosSection
                                                photos={photos}
                                                handlePhotoUpload={handlePhotoUpload}
                                                removePhoto={removePhoto}
                                            />
                                            <Divider sx={{my: 4}}/>

                                            <IssueSummarySection
                                                problemTypes={problemTypes}
                                                issues={issues}
                                                selectedProblemType={selectedProblemType}
                                                selectedIssue={selectedIssue}
                                                selectedPriority={selectedPriority}
                                                editorState={editorState}
                                            />
                                        </>
                                    )
                                )}

                                {/* Action Buttons */}
                                <Box
                                    display="flex"
                                    justifyContent="flex-end"
                                    alignItems="center"
                                    gap={2}
                                    mt={4}
                                >
                                    {activeStep === 0 ? (
                                        <>
                                            <MuiButton
                                                variant="outlined"
                                                color="error"
                                                onClick={handleReset}
                                                disabled={isSubmitting}
                                                sx={{
                                                    borderRadius: '8px',
                                                    textTransform: 'none',
                                                    px: 3
                                                }}
                                            >
                                                Cancel
                                            </MuiButton>

                                            <MuiButton
                                                variant="contained"
                                                onClick={handleNext}
                                                disabled={isSubmitting}
                                                sx={{
                                                    borderRadius: '8px',
                                                    backgroundColor: '#398DD2',
                                                    textTransform: 'none',
                                                    boxShadow: 'none',
                                                    px: 3,
                                                    '&:hover': {
                                                        backgroundColor: '#2b78be',
                                                        boxShadow: 'none'
                                                    }
                                                }}
                                            >
                                                Continue
                                            </MuiButton>
                                        </>
                                    ) : submitSuccess ?  (
                                        <MuiButton
                                            variant="contained"
                                            onClick={() => navigate('/tickets')}
                                            sx={{
                                                borderRadius: '8px',
                                                backgroundColor: '#398DD2',
                                                textTransform: 'none',
                                                boxShadow: 'none',
                                                px: 3,
                                                '&:hover': {
                                                    backgroundColor: '#2b78be',
                                                    boxShadow: 'none'
                                                }
                                            }}
                                        >
                                            View My Issues
                                        </MuiButton>
                                    ) : (
                                        <>
                                            <MuiButton
                                                variant="outlined"
                                                onClick={handleBack}
                                                disabled={isSubmitting}
                                                sx={{
                                                    borderRadius: '8px',
                                                    textTransform: 'none',
                                                    px: 3
                                                }}
                                            >
                                                Back
                                            </MuiButton>

                                            <MuiButton
                                                variant="contained"
                                                onClick={handleNext}
                                                disabled={isSubmitting}
                                                sx={{
                                                    borderRadius: '8px',
                                                    backgroundColor: '#398DD2',
                                                    textTransform: 'none',
                                                    boxShadow: 'none',
                                                    px: 3,
                                                    '&:hover': {
                                                        backgroundColor: '#2b78be',
                                                        boxShadow: 'none'
                                                    }
                                                }}
                                            >
                                                Submit Issue
                                            </MuiButton>
                                        </>
                                    )}
                                </Box>
                            </Form>
                        )}
                    </Formik>
                </CardContent>
            </Card>
        </Box>
    );
};

export default ReportIssueModernClaude;